import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const mobileAppsConfig: PlatformSubpageConfig = {
  slug: 'mobile-apps',
  path: '/platform/mobile-apps',
  breadcrumbLabel: 'Mobile Apps',
  hero: {
    title: 'Launch branded iOS and Android apps in weeks',
    subtitle:
      'White-label mobile experiences with custom themes, real-time control, and scene automation.',
    ...defaultCtas,
  },
  heroImage: img('/images/home/white-label-app-image.png', 'White-label smart home mobile app'),
  eduIntro: {
    title: 'What are white-label mobile apps?',
    description:
      'White-label apps let you ship branded iOS and Android control experiences under your logo and colors—powered by a proven smart-home platform without building mobile teams from scratch.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Your brand, our battle-tested app platform',
    items: [
      { icon: 'smartphone', title: 'Launch Faster', description: 'Go to market with white-label iOS and Android apps in weeks, not quarters.' },
      { icon: 'layers', title: 'Fully Custom Branding', description: 'Themes, logos, and UX flows that feel native to your product line.' },
      { icon: 'zap', title: 'Automation Ready', description: 'Scenes, schedules, and sharing features users expect from day one.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'APP EXPERIENCE',
    title: 'Preview your branded app experience',
    subtitle: 'Switch between light and dark themes on a live-quality mockup.',
    items: [
      {
        title: 'Custom branding',
        description: 'Your colors, typography, and app store assets—polished for consumer expectations.',
        image: img('/images/home/white-label-app-image.png', 'Branded app'),
        cta: { label: 'See branding', to: '/contact-us' },
      },
      {
        title: 'Real-time device control',
        description: 'Instant device sync, push notifications, and responsive in-app experiences.',
        image: img('/images/home/partner-dashboard.png', 'Real-time control'),
        cta: { label: 'Explore control', to: '/contact-us' },
      },
      {
        title: 'Scenes and automation',
        description: 'One-tap scenes, schedules, and device sharing for households and commercial sites.',
        image: img('/images/home/feature-cloud.jpg', 'Automation'),
        cta: { label: 'View automation', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'Mobile experiences your customers will love',
    items: [
      { icon: 'smartphone', title: 'Custom Branding', description: 'Your colors, typography, and app store assets.' },
      { icon: 'layers', title: 'Dark Mode', description: 'Polished light and dark themes out of the box.' },
      { icon: 'zap', title: 'Push Notifications', description: 'Alerts for events, automations, and device status.' },
      { icon: 'refresh', title: 'Real-Time Sync', description: 'Sub-second UI updates across rooms and devices.' },
      { icon: 'key', title: 'Device Sharing', description: 'Invite family members and manage home access.' },
      { icon: 'activity', title: 'Scene Automation', description: 'One-tap scenes and scheduled routines.' },
    ],
  },
  ctaBanner: {
    title: 'Launch your branded mobile app on our proven platform',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'Do you support both iOS and Android?', answer: 'Yes—white-label apps for both platforms with shared feature parity.' },
      { question: 'Can we customize branding?', answer: 'Full theme, logo, and store listing customization is supported.' },
      { question: 'How fast can we launch?', answer: 'Most partners launch in weeks using our pre-built app shell and SDK.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Preview your branded app experience',
    subtitle: 'Switch between light and dark themes on a live mockup.',
  },
};
